(ns hai.tasks.detail
  "Full-page view of a single task: all fields untruncated, plus
   complete / edit / delete actions gated by the viewer's permissions."
  (:require
   [hai.notes.data :as notes-data]
   [hai.notes.link-combobox :as link-combobox]
   [hai.notes.sections :as sections]
   [hai.sharing.helper :as sharing]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.ui :as ui]
   [hai.tasks.view :as view]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private edit-icon (icons/mui-icon :outlined :edit))
(def ^:private trash-icon (icons/mui-icon :outlined :delete))
(def ^:private check-icon (icons/mui-icon :outlined :check))
(def ^:private undo-icon (icons/mui-icon :outlined :undo))
(def ^:private person-icon (icons/mui-icon :outlined :person))

(defsub ::window-title
  [db]
  (let [task-uuid (get-in db [:routing/current-route :path-params :task/uuid])
        task (when task-uuid (get-in db [:task/uuid (uuid (str task-uuid))]))]
    (or (:task/title task) "Task")))

;; Tracks which tasks a resolve has completed for, so the page can tell
;; "still loading" apart from "not found / no access" — both of which leave a
;; nil entity in the store.
(defevent ::mark-resolved [db task-uuid]
  (update db ::resolved-uuids (fnil conj #{}) (uuid (str task-uuid))))

(defsub ::resolved?
  [db task-uuid]
  (boolean (and task-uuid
                (contains? (get db ::resolved-uuids #{}) (uuid (str task-uuid))))))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

(o/defstyled panel :div
  :flex :flex-col
  {:gap "24px" :max-width "760px"})

(o/defstyled action-bar :div
  :flex :items-center
  {:gap "10px" :flex-wrap "wrap"})

(o/defstyled action-button :button
  :inline-flex :items-center
  {:gap "6px"
   :padding "8px 14px"
   :border "none" :border-radius "8px"
   :font-family "inherit" :font-size "13px" :font-weight "600"
   :cursor "pointer"
   :transition "background 0.15s ease, opacity 0.15s ease, color 0.15s ease"}
  [:svg {:width "16px" :height "16px" :fill "currentColor"}]
  [:&.primary {:background t/--card-forest :color "white"}
   [:&:hover {:opacity "0.9"}]]
  [:&.ghost {:background t/--card-forest-10 :color t/--card-forest}
   [:&:hover {:background t/--card-forest-14}]]
  [:&.danger {:background t/--card-error-12 :color t/--card-error}
   [:&:hover {:background t/--card-error :color "white"}]])

(o/defstyled delete-confirm :div
  :flex :items-center
  {:gap "12px" :padding "12px 16px"
   :border-radius "10px"
   :background t/--card-error-12
   :color t/--card-error}
  [:.prompt {:margin-right "auto" :font-size "14px" :font-weight "500"}])

(o/defstyled field :div
  :flex :flex-col
  {:gap "6px"})

(o/defstyled field-label :div
  {:font-size "11px" :font-weight "700" :letter-spacing "0.06em"
   :text-transform "uppercase" :color t/--card-forest-70})

(o/defstyled description-body :p
  {:margin "0" :white-space "pre-wrap" :overflow-wrap "anywhere"
   :color t/--card-forest :font-size "15px" :line-height "1.6"})

(o/defstyled deadline-exact :div
  {:margin-top "4px" :font-size "13px" :color t/--card-forest-70})

(o/defstyled muted :span
  {:color t/--card-forest-70 :font-size "14px"})

(o/defstyled loading-state :div
  :flex :items-center
  {:gap "10px" :padding "8px 2px" :color t/--card-forest-70})

(o/defstyled assignee :div
  :flex :items-center
  {:gap "10px"}
  [:.avatar
   :inline-flex :items-center :justify-center :flex-shrink-0
   {:width "36px" :height "36px" :border-radius "50%"
    :background t/--card-cream :color t/--card-forest
    :font-size "13px" :font-weight "700"}
   [:svg {:width "20px" :height "20px"}]
   [:&.me
    {:background "transparent"
     :box-shadow "inset 0 0 0 1px var(--task-accent)"
     :color      t/--task-accent}]]
  [:.meta :flex :flex-col {:gap "1px"}]
  [:.assignee-name {:font-size "15px" :font-weight "600" :color t/--card-forest}]
  [:.assignee-email {:font-size "13px" :color t/--card-forest-70}])

;; ---------------------------------------------------------------------------
;; View
;; ---------------------------------------------------------------------------

(defn- completed-label [task]
  (str "Completed "
       (.toLocaleDateString (js/Date. (:task/completed-at task))
                            "en-US" #js {:month "short" :day "numeric" :year "numeric"})))

(defn- assignee-block [assigned-to]
  (if assigned-to
    (let [me? (data/assignee-me? @(<s :auth/identity) assigned-to)
          full-name (not-empty (:user/full-name assigned-to))
          email (data/assignee-email assigned-to)]
      [assignee
       [:span.avatar {:class (when me? "me")}
        (or (data/assignee-initials assigned-to) [person-icon])]
       [:span.meta
        [:span.assignee-name (or full-name email "Unassigned")]
        (when (and full-name email)
          [:span.assignee-email email])]])
    [muted "Unassigned"]))

(defn- task-view [task]
  (r/with-let [!editing?      (r/atom false)
               !confirming?   (r/atom false)
               ;; Which "Linked" adder is open (`:links`). Local to the detail
               ;; view — the section is always live, independent of the edit
               ;; form's `!editing?` toggle.
               !open-sections (r/atom #{})]
    (let [task-uuid   (:task/uuid task)
          title       (:task/title task)
          description (:task/description task)
          links       (:task/activities task)
          link-urls   (vec (keep :activity/url links))
          done?       (some? (:task/completed-at task))
          can-edit?   (sharing/can-edit? task)
          can-delete? (sharing/can-delete? task)
          save-edit (fn [{:keys [title description urgency deadline urls assignee]}]
                      (>e [::data/update {:task-uuid task-uuid
                                          :title title
                                          :description description
                                          :urgency urgency
                                          :deadline deadline
                                          :urls urls
                                          :clear-deadline? (and (:task/deadline-at task)
                                                                (nil? (:date deadline)))}])
                      ;; Reassignment is its own mutation (`task/assign` resolves
                      ;; the assignee to a Humble user and grants them access), so
                      ;; it goes separately — and only when a fresh assignee was
                      ;; picked (a contact or a typed email); an untouched picker
                      ;; still holds the current assignee.
                      (when (data/picked-assignee? assignee)
                        (>e [::data/assign task-uuid assignee]))
                      (reset! !editing? false))
          toggle-done (fn []
                        (>e [(if done? ::data/uncomplete ::data/complete) task-uuid]))
          delete! (fn []
                    (reset! !confirming? false)
                    (>e [::data/delete task-uuid])
                    (>e [:routing/navigate [:tasks/view]]))]
      (if @!editing?
        [ui/quick-add {:class "editing"}
         [ui/task-form {:form-id [::edit-form task-uuid]
                        :initial-value (cond-> {:task/title title}
                                         (seq description) (assoc :task/description description)
                                         (:task/urgency task) (assoc :task/urgency (:task/urgency task))
                                         (:task/deadline-at task) (assoc :task/deadline (deadline/from-task task))
                                         (seq link-urls) (assoc :activity/urls link-urls))
                        :assignable? true
                        :initial-assignee (:task/assigned-to task)
                        :auto-focus? true
                        :placeholder "Title"
                        :primary-label "save"
                        :on-save save-edit
                        :on-cancel #(reset! !editing? false)}]]
        [panel
         (when (or can-edit? can-delete?)
           [action-bar
            (when can-edit?
              [action-button {:class "primary" :type "button" :on-click toggle-done}
               (if done? [undo-icon] [check-icon])
               (if done? "Mark as not done" "Mark complete")])
            (when can-edit?
              [action-button {:class "ghost" :type "button" :on-click #(reset! !editing? true)}
               [edit-icon] "Edit"])
            (when can-delete?
              [action-button {:class "danger" :type "button" :on-click #(reset! !confirming? true)}
               [trash-icon] "Delete"])])
         (when @!confirming?
           [delete-confirm
            [:span.prompt "Delete this task? This can't be undone."]
            [action-button {:class "ghost" :type "button" :on-click #(reset! !confirming? false)}
             "Cancel"]
            [action-button {:class "danger" :type "button" :on-click delete!}
             "Delete"]])
         [field
          [field-label "Status"]
          (if done?
            [:span (completed-label task)]
            [muted "Open"])]
         [field
          [field-label "Deadline"]
          (if (:task/deadline-at task)
            [:div
             [view/task-deadline {:task task}]
             [deadline-exact (deadline/tooltip task)]]
            [muted "No deadline"])]
         (when (seq description)
           [field
            [field-label "Description"]
            [description-body description]])
         (when (seq links)
           [field
            [field-label "Links"]
            [view/task-links {:links links}]])
         ;; Linked contacts / notes / tasks. Editors get the always-on adder
         ;; (independent of the form's edit toggle); read-only viewers still see
         ;; the chips (clickable, no remove ×). Hidden entirely when there's
         ;; nothing to link and nothing linked.
         (when (or can-edit?
                   (seq (:task/contacts task))
                   (seq (:task/notes task))
                   (seq (:task/tasks task)))
           [field
            [field-label "Linked"]
            [sections/entity-extras
             {:entity-uuid          task-uuid
              :link-copy            "Attach a contact, note, task or page to this task"
              :editing?             can-edit?
              :link-urls            []
              :contacts             (:task/contacts task)
              :linked-notes         (:task/notes task)
              :linked-tasks         (:task/tasks task)
              :open-set             @!open-sections
              :open-section!        #(swap! !open-sections conj %)
              :close-section!       #(swap! !open-sections disj %)
              ;; A URL pasted into the ＋ link field lands as a task activity.
              :on-add-link          (fn [url]
                                      (>e [::data/add-url-link {:task/uuid task-uuid :url url}]))
              :on-add-contact       (fn [contact]
                                      (>e [::data/add-contact {:task/uuid task-uuid
                                                               :contact/uuid (:contact/uuid contact)}]))
              :on-remove-contact    (fn [contact]
                                      (>e [::data/remove-contact {:task/uuid task-uuid
                                                                  :contact/uuid (:contact/uuid contact)}]))
              :on-add-note          (fn [note]
                                      (>e [::data/add-note-link {:task/uuid task-uuid
                                                                 :note/uuid (:note/uuid note)}]))
              :on-remove-note       (fn [note]
                                      (>e [::data/remove-note-link {:task/uuid task-uuid
                                                                    :note/uuid (:note/uuid note)}]))
              :on-add-task          (fn [linked]
                                      (>e [::data/add-task-link {:task/uuid task-uuid
                                                                 :task/linked-uuid (:task/uuid linked)}]))
              :on-remove-task       (fn [linked]
                                      (>e [::data/remove-task-link {:task/uuid task-uuid
                                                                    :task/linked-uuid (:task/uuid linked)}]))
              ;; Clicking a chip routes to that entity's own page — reuse the
              ;; note surface's shared routing (contact→edit w/ return-to,
              ;; task→detail, note→detail).
              :on-open-entity       (fn [entity] (>e [::notes-data/open-entity entity]))
              :render-entity-search link-combobox/entity-search}]])
         [field
          [field-label "Assigned to"]
          [assignee-block (:task/assigned-to task)]]]))))

(defn page [_route]
  (let [params    @(<s [:routing/path-params])
        task      @(<s [:entity params])
        resolved? @(<s [::resolved? (:task/uuid params)])]
    [layout/page
     [layout/breadcrumbs]
     [layout/heading {:text (or (:task/title task) "Task")}]
     [layout/content
      (cond
        task      ^{:key (:task/uuid task)} [task-view task]
        resolved? [muted "This task doesn't exist or you no longer have access to it."]
        :else     [loading-state [c/Spinner {:size "3"}] "Loading…"])]]))
